import { type SolarLatLng } from '@payaca/components/solarRoofView/SolarRoofView';
import { DesignerTool } from './types';

type RenderPackedPanel = {
  corners: SolarLatLng[];
  faceIndex: number;
  segmentIndex: number;
  orientation: 'LANDSCAPE' | 'PORTRAIT';
  annualAcKwh: number;
};

export type DeriveRoofViewPropsArgs = {
  geometryMode: boolean;
  tool: DesignerTool;
  /** Off = inspect the bare aerial before/while panels are plotted. */
  showPanelsLayer: boolean;
  hasFootprints: boolean;
  footprintRings: SolarLatLng[][];
  renderPackedPanels: RenderPackedPanel[];
  selectedFaceIndexForView: number | null;
  /** Title-parcel boundary ring, when the geometry captured one. */
  parcelRing?: SolarLatLng[] | null;
};

/**
 * The single place the tool model is resolved into the SolarRoofView prop
 * values. One tool is active at a time (the rail is the only mode switcher);
 * everything here is a pure view of (tool, layer toggle, selection):
 *  - 'select' — faces selectable/directly editable (vertex handles + grab),
 *    obstacles and panels clickable-to-select.
 *  - 'draw' / 'obstacle' — the draw owns the map clicks: faces drop to
 *    read-only context, panel clicks off.
 *  - 'move' — drags translate the whole model rigidly.
 *  - 'trace' — the OSM footprints become clickable.
 *  - 'pin' — the reposition pin owns the map; everything else read-only.
 */
export const deriveRoofViewProps = ({
  geometryMode,
  tool,
  showPanelsLayer,
  hasFootprints,
  footprintRings,
  renderPackedPanels,
  selectedFaceIndexForView,
  parcelRing,
}: DeriveRoofViewPropsArgs) => {
  const selectTool = geometryMode && tool === 'select';
  const addingFace = geometryMode && tool === 'draw';
  const addingObstacle = geometryMode && tool === 'obstacle';
  const movingRoof = geometryMode && tool === 'move';
  const tracing = geometryMode && tool === 'trace';
  const pinning = tool === 'pin';

  let packedPanelsForView: RenderPackedPanel[] | undefined;
  if (geometryMode) {
    packedPanelsForView = showPanelsLayer ? renderPackedPanels : [];
  } else {
    packedPanelsForView = undefined;
  }

  const showFootprints =
    hasFootprints &&
    (tracing || pinning || !geometryMode || (geometryMode && !showPanelsLayer));
  const footprintsForView = showFootprints ? footprintRings : undefined;
  const footprintPickActive = tracing && hasFootprints;

  const obstacleEditMode = selectTool || addingObstacle;
  // Faces must not capture clicks while a draw tool is armed — with auto-fit
  // zoom the model fills the map, so nearly every corner click would
  // otherwise land on a face and cancel the draw.
  const faceEditMode: 'select' | undefined = selectTool ? 'select' : undefined;
  const staticFaceOutlines = addingFace || addingObstacle || tracing || pinning;
  const boundaryEditFaceIndex = selectTool ? selectedFaceIndexForView : null;
  const grabFacesForView = selectTool;
  const moveWholeRoofForView = movingRoof;
  const repositionForView = pinning;
  const panelClickDisabled = !geometryMode ? pinning : tool !== 'select';

  // The legal property line: it explains an excluded far half of a merged
  // building.
  const parcelForView =
    geometryMode && parcelRing?.length ? parcelRing : undefined;

  const showAllFaceOutlines = geometryMode;

  return {
    addingFace,
    addingObstacle,
    packedPanelsForView,
    showFootprints,
    footprintsForView,
    footprintPickActive,
    obstacleEditMode,
    faceEditMode,
    staticFaceOutlines,
    boundaryEditFaceIndex,
    repositionForView,
    panelClickDisabled,
    parcelForView,
    showAllFaceOutlines,
    grabFacesForView,
    moveWholeRoofForView,
  };
};
